import { useCallback, useRef, useState } from 'react';
import { resolvePrimaryRole } from '../utils/roleRouting';
import { loadHelpVideos, filterHelpVideos, roleTokenFor, normalizeProfileGrade } from './helpVideos';

/**
 * useHelpVideos — the How-to videos state shared by the tour provider and
 * the videos-only provider (helpVideos flag without onboardingTour): one
 * appConfig/helpVideos read per session, on demand, filtered by role/grade;
 * the panel + player open/close state and their focus-return refs.
 *
 * `enabled` false (helpVideos flag off) makes every action inert: no read
 * ever happens, the list stays empty (so the tour's last-step Watch button
 * never appears) and the panel/player never open. Lifted verbatim out of
 * useOnboardingTour in the flag split — the tour hook's behavior is
 * unchanged.
 */
export function useHelpVideos({ userRef, enabled = true }) {
  const [videos, setVideos] = useState({ status: 'idle', list: [] });
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const videosRef = useRef(videos);
  const videosPromiseRef = useRef(null);
  const panelReturnRef = useRef(null);
  const playerReturnRef = useRef(null);

  // ── one doc read, on demand, cached per session ────────────────────────
  const prefetchHelpVideos = useCallback(() => {
    if (!enabled) return Promise.resolve([]);
    if (videosRef.current.status !== 'idle') return videosPromiseRef.current || Promise.resolve(videosRef.current.list);
    videosRef.current = { status: 'loading', list: [] };
    setVideos(videosRef.current);
    const finish = (list) => {
      const next = { status: 'loaded', list };
      videosRef.current = next;
      setVideos(next);
      return list;
    };
    const p = loadHelpVideos()
      .then(({ videos: raw }) => {
        const u = userRef.current;
        const primary = resolvePrimaryRole(u && u.role);
        return finish(filterHelpVideos(raw, {
          roleToken: roleTokenFor(primary),
          gradeToken: normalizeProfileGrade(u && u.profile && u.profile.grade),
          applyGrade: primary === 'teacher',
        }));
      })
      .catch(() => finish([]));
    videosPromiseRef.current = p;
    return p;
  }, [enabled, userRef]);

  const openHelpVideos = useCallback((returnFocusTo) => {
    if (!enabled) return;
    panelReturnRef.current = returnFocusTo || (typeof document !== 'undefined' ? document.activeElement : null);
    prefetchHelpVideos();
    setPanelOpen(true);
  }, [enabled, prefetchHelpVideos]);

  const closeHelpVideos = useCallback(() => setPanelOpen(false), []);

  const playVideo = useCallback((video, returnFocusTo) => {
    if (!enabled) return;
    playerReturnRef.current = returnFocusTo || null;
    setActiveVideo(video);
  }, [enabled]);

  const closePlayer = useCallback(() => setActiveVideo(null), []);

  return {
    videos,
    videosRef,
    panelOpen,
    activeVideo,
    prefetchHelpVideos,
    openHelpVideos,
    closeHelpVideos,
    playVideo,
    closePlayer,
    panelReturnRef,
    playerReturnRef,
  };
}
